import type { Panel } from "@electro-planner/domain";
import { CURRENT_VERSION } from "../releases.ts";
import type { LinkedProject } from "../linkedProjects/registry.ts";
import { DocumentSwitcher } from "./DocumentSwitcher.tsx";

/**
 * Toolbar längst upp i centralspec-vyn. Ankare för doc-switcher, titel-fält,
 * export-knappar, release-notes-knapp och vy-toggle (tabell ↔ DIN-layout).
 */
export function PanelToolbar({
  documentId,
  panel,
  tab,
  linkedProject,
  onTabChange,
  onTitleChange,
  onDesignationChange,
  onBusbarChange,
  onEarthingChange,
  onStatusChange,
  onSwitchDocument,
  onOpenRelease,
  onOpenLink,
  onOpenAbbrev,
  onOpenProjectInfo,
  onOpenRevisions,
  onOpenSymbolPicker,
  onShare,
  onExportPdf,
  onExportXlsx,
}: {
  documentId: string;
  panel: Panel | undefined;
  tab: "table" | "layout" | "sld";
  linkedProject: LinkedProject | null;
  onTabChange: (t: "table" | "layout" | "sld") => void;
  onTitleChange: (v: string) => void;
  onDesignationChange: (v: string) => void;
  onBusbarChange: (v: number | undefined) => void;
  onEarthingChange: (v: Panel["earthingSystem"]) => void;
  onStatusChange: (v: Panel["status"]) => void;
  onSwitchDocument: (id: string) => void;
  onOpenRelease: () => void;
  onOpenLink: () => void;
  onOpenAbbrev: () => void;
  onOpenProjectInfo: () => void;
  onOpenRevisions: () => void;
  onOpenSymbolPicker: () => void;
  onShare: () => void;
  onExportPdf: () => void;
  onExportXlsx: () => void;
}) {
  const status = panel?.status ?? "draft";
  const earthing = panel?.earthingSystem ?? "";
  return (
    <div className="no-print flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
      <DocumentSwitcher
        currentId={documentId}
        currentTitle={panel?.title ?? "Central"}
        onSwitch={onSwitchDocument}
      />

      <div className="flex items-center gap-1">
        <label className="text-xs text-slate-500">Beteckning</label>
        <input
          type="text"
          value={panel?.designation ?? ""}
          onChange={(e) => onDesignationChange(e.target.value)}
          placeholder="CK1"
          className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
        />
      </div>

      <div className="flex flex-1 items-center gap-1">
        <label className="text-xs text-slate-500">Titel</label>
        <input
          type="text"
          value={panel?.title ?? ""}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Huvudcentral"
          className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
        />
      </div>

      <div className="flex items-center gap-1">
        <label className="text-xs text-slate-500" title="Huvudskenans märkström">
          Skena
        </label>
        <input
          type="number"
          value={panel?.busbarAmps ?? ""}
          onChange={(e) => {
            const n = parseFloat(e.target.value);
            onBusbarChange(Number.isFinite(n) ? n : undefined);
          }}
          placeholder="63"
          min={0}
          className="w-16 rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <span className="text-xs text-slate-500">A</span>
      </div>

      <div className="flex items-center gap-1">
        <label className="text-xs text-slate-500" title="Jordningssystem (inkommande servis)">
          Servis
        </label>
        <select
          value={earthing}
          onChange={(e) =>
            onEarthingChange(
              e.target.value === ""
                ? undefined
                : (e.target.value as Panel["earthingSystem"]),
            )
          }
          className="rounded border border-slate-300 bg-white px-1 py-1 text-xs"
        >
          <option value="">—</option>
          <option value="TN-S">TN-S</option>
          <option value="TN-C">TN-C</option>
          <option value="TN-C-S">TN-C-S</option>
          <option value="TT">TT</option>
          <option value="IT">IT</option>
        </select>
      </div>

      <StatusBadge status={status} onChange={onStatusChange} />

      <div className="flex overflow-hidden rounded border border-slate-300 text-sm">
        <button
          type="button"
          onClick={() => onTabChange("table")}
          className={`px-3 py-1 ${
            tab === "table"
              ? "bg-sky-600 text-white"
              : "bg-white text-slate-700 hover:bg-slate-50"
          }`}
          title="Redigera tabellen"
        >
          Tabell
        </button>
        <button
          type="button"
          onClick={() => onTabChange("layout")}
          className={`px-3 py-1 ${
            tab === "layout"
              ? "bg-sky-600 text-white"
              : "bg-white text-slate-700 hover:bg-slate-50"
          }`}
          title="Visualisera DIN-skenor"
        >
          Layout
        </button>
        <button
          type="button"
          onClick={() => onTabChange("sld")}
          className={`px-3 py-1 ${
            tab === "sld"
              ? "bg-sky-600 text-white"
              : "bg-white text-slate-700 hover:bg-slate-50"
          }`}
          title="Enlinjeschema (CAD-vy)"
        >
          Enlinje
        </button>
      </div>

      <button
        type="button"
        onClick={onOpenSymbolPicker}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50"
        title="Symbolpalett — välj komponenter visuellt"
        aria-label="Symbolpalett"
      >
        ▦
      </button>

      <button
        type="button"
        onClick={onExportPdf}
        className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
        title="Skriv ut / spara som PDF"
      >
        PDF
      </button>
      <button
        type="button"
        onClick={onExportXlsx}
        className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
        title="Exportera till Excel"
      >
        Excel
      </button>
      <button
        type="button"
        onClick={onShare}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50"
        title="Dela (exportera .panelspec-fil)"
      >
        Dela
      </button>
      <button
        type="button"
        onClick={onOpenProjectInfo}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50"
        title="Projektinformation"
      >
        Info
      </button>
      <button
        type="button"
        onClick={onOpenRevisions}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50"
        title="Revisionshistorik"
      >
        Rev.
      </button>
      <button
        type="button"
        onClick={onOpenAbbrev}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50"
        title="Förkortningar & genvägar (?)"
        aria-label="Förkortningar"
      >
        ?
      </button>
      <button
        type="button"
        onClick={onOpenLink}
        className={`rounded border px-3 py-1 text-sm ${
          linkedProject
            ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        }`}
        title={linkedProject ? `Kopplad till ${linkedProject.projectTitle || "schemaeditor-projekt"} (${linkedProject.devices.length} apparater)` : "Koppla till ett schemaeditor-projekt"}
      >
        {linkedProject ? `🔗 ${linkedProject.devices.length} apparater` : "Koppla till projekt"}
      </button>
      <button
        type="button"
        onClick={onOpenRelease}
        className="rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-500 hover:bg-slate-50"
        title="Vad är nytt"
      >
        v{CURRENT_VERSION}
      </button>
    </div>
  );
}

/** Trafikljus-badge för panel-status. Klick växlar genom draft → review → done. */
function StatusBadge({
  status,
  onChange,
}: {
  status: NonNullable<Panel["status"]>;
  onChange: (v: Panel["status"]) => void;
}) {
  const cycle: Record<NonNullable<Panel["status"]>, NonNullable<Panel["status"]>> = {
    draft: "review",
    review: "done",
    done: "draft",
  };
  const label: Record<NonNullable<Panel["status"]>, string> = {
    draft: "Utkast",
    review: "Granskning",
    done: "Klar",
  };
  const color: Record<NonNullable<Panel["status"]>, string> = {
    draft: "border-slate-300 bg-slate-50 text-slate-700",
    review: "border-amber-300 bg-amber-50 text-amber-800",
    done: "border-emerald-300 bg-emerald-50 text-emerald-800",
  };
  return (
    <button
      type="button"
      onClick={() => onChange(cycle[status])}
      className={`rounded border px-2 py-1 text-xs font-medium ${color[status]}`}
      title="Ändra status (klick växlar genom Utkast → Granskning → Klar)"
    >
      {label[status]}
    </button>
  );
}
